import type {
  BppSearchConfig,
  CtaPresentation,
  EmbedUi,
  InstanceConfig,
  PropertyTab,
  SearchMode,
  TriggerConfig,
} from "./types"

const PROPERTY_TABS: PropertyTab[] = ["buy", "rent", "commercial"]
const EMBED_UIS: EmbedUi[] = ["lightbox", "dropdown"]

function assertSearchBaseUrlProtocol(url: string): void {
  const { protocol, hostname } = new URL(url)
  if (protocol === "https:") {
    return
  }
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return
  }
  throw new Error(
    "[BppSearch] searchBaseUrl must use https:// (http is only allowed on localhost)"
  )
}

function normalizeSearchBaseUrl(url: string): string {
  const trimmed = url.trim().replace(/\/+$/, "")
  if (!trimmed.startsWith("http://") && !trimmed.startsWith("https://")) {
    throw new Error("[BppSearch] searchBaseUrl must be an absolute URL")
  }
  try {
    new URL(trimmed)
  } catch {
    throw new Error("[BppSearch] searchBaseUrl is not a valid URL")
  }
  assertSearchBaseUrlProtocol(trimmed)
  return trimmed
}

function normalizeEnabledPropertyTabs(
  tabs: PropertyTab[] | undefined
): PropertyTab[] | undefined {
  if (!tabs || tabs.length === 0) {
    return undefined
  }

  const enabled = new Set<PropertyTab>()
  for (const tab of tabs) {
    if (PROPERTY_TABS.includes(tab)) {
      enabled.add(tab)
    }
  }

  if (enabled.size === 0) {
    return undefined
  }

  return PROPERTY_TABS.filter((tab) => enabled.has(tab))
}

/**
 * Resolves which property tab drives search params and whether toggles render.
 * Omit `tabs` (or pass an empty array) to search as Buy with no toggle UI.
 */
export function resolvePropertyTabConfig(
  defaultTab: PropertyTab | undefined,
  tabs: PropertyTab[] | undefined
): { defaultTab: PropertyTab; tabs?: PropertyTab[] } {
  const enabledTabs = normalizeEnabledPropertyTabs(tabs)

  if (!enabledTabs) {
    return { defaultTab: "buy", tabs: undefined }
  }

  if (enabledTabs.length === 1) {
    const onlyTab = enabledTabs[0]
    if (!onlyTab) {
      return { defaultTab: "buy", tabs: undefined }
    }
    return { defaultTab: onlyTab, tabs: undefined }
  }

  const firstTab = enabledTabs[0]
  if (!firstTab) {
    return { defaultTab: "buy", tabs: undefined }
  }

  const resolvedDefault =
    defaultTab && enabledTabs.includes(defaultTab) ? defaultTab : firstTab

  return { defaultTab: resolvedDefault, tabs: enabledTabs }
}

function validatePropertyTabs(tabs: PropertyTab[] | undefined): PropertyTab[] | undefined {
  if (!tabs) {
    return undefined
  }
  const invalid = tabs.filter((tab) => !PROPERTY_TABS.includes(tab))
  if (invalid.length > 0) {
    throw new Error(
      `[BppSearch] Invalid property tabs: ${invalid.join(", ")}. Use buy, rent, commercial only.`
    )
  }
  return tabs
}

function normalizePropertyFields(
  defaultTab: PropertyTab | undefined,
  tabs: PropertyTab[] | undefined
): { defaultTab: PropertyTab; tabs?: PropertyTab[] } {
  validatePropertyTabs(tabs)
  return resolvePropertyTabConfig(defaultTab, tabs)
}

/** Instance/trigger value wins over the root default, which falls back to `lightbox`. */
function resolveUi(ui: EmbedUi | undefined, rootUi: EmbedUi | undefined): EmbedUi {
  const resolved = ui ?? rootUi ?? "lightbox"
  if (!EMBED_UIS.includes(resolved)) {
    throw new Error(
      `[BppSearch] Invalid ui: ${resolved}. Use ${EMBED_UIS.join(" or ")} only.`
    )
  }
  return resolved
}

function normalizeTrigger(trigger: TriggerConfig, rootUi: EmbedUi | undefined): TriggerConfig {
  const ui = resolveUi(trigger.ui, rootUi)
  if (trigger.mode !== "property") {
    return { ...trigger, ui }
  }
  const { defaultTab, tabs } = normalizePropertyFields(trigger.defaultTab, trigger.tabs)
  return {
    ...trigger,
    ui,
    defaultTab,
    tabs,
  }
}

function normalizeInstance(
  instance: InstanceConfig,
  rootUi: EmbedUi | undefined
): InstanceConfig {
  const ui = resolveUi(instance.ui, rootUi)
  if (instance.mode !== "property") {
    return { ...instance, ui }
  }
  const { defaultTab, tabs } = normalizePropertyFields(instance.defaultTab, instance.tabs)
  return {
    ...instance,
    ui,
    defaultTab,
    tabs,
  }
}

export type NormalizedBppSearchConfig = Omit<
  BppSearchConfig,
  "searchBaseUrl" | "triggers" | "instances" | "openInNewTab"
> & {
  searchBaseUrl: string
  ctaPresentation: CtaPresentation
  triggers: TriggerConfig[]
  instances: InstanceConfig[]
  triggerRetryMs: number
  triggerRetryTimeoutMs: number
  zIndex: number
  openInNewTab: boolean
}

export function normalizeConfig(config: BppSearchConfig): NormalizedBppSearchConfig {
  const searchBaseUrl = normalizeSearchBaseUrl(config.searchBaseUrl)
  const ctaPresentation = config.ctaPresentation

  if (ctaPresentation === "external") {
    const triggers =
      config.triggers?.map((trigger) => normalizeTrigger(trigger, config.ui)) ?? []
    if (triggers.length === 0 && config.mount && config.mode) {
      triggers.push(
        normalizeTrigger(
          {
            selector: config.mount,
            mode: config.mode,
            defaultTab: config.defaultTab,
            tabs: config.tabs,
            ui: config.ui,
            placeholder: config.placeholder,
            zIndex: config.zIndex,
          },
          config.ui
        )
      )
    }
    if (triggers.length === 0) {
      throw new Error("[BppSearch] external mode requires triggers[]")
    }
    return {
      ...config,
      searchBaseUrl,
      ctaPresentation,
      triggers,
      instances: [],
      triggerRetryMs: config.triggerRetryMs ?? 0,
      triggerRetryTimeoutMs: config.triggerRetryTimeoutMs ?? 10000,
      zIndex: config.zIndex ?? 999999,
      openInNewTab: config.openInNewTab === true,
    }
  }

  const instances =
    config.instances?.map((instance) => normalizeInstance(instance, config.ui)) ??
    (config.mount && config.mode
      ? [
          normalizeInstance(
            {
              mount: config.mount,
              mode: config.mode,
              defaultTab: config.defaultTab,
              tabs: config.tabs,
              ui: config.ui,
              placeholder: config.placeholder,
              zIndex: config.zIndex,
            },
            config.ui
          ),
        ]
      : [])

  if (instances.length === 0) {
    throw new Error("[BppSearch] embedded mode requires instances[] or mount + mode")
  }

  return {
    ...config,
    searchBaseUrl,
    ctaPresentation,
    triggers: [],
    instances,
    triggerRetryMs: config.triggerRetryMs ?? 0,
    triggerRetryTimeoutMs: config.triggerRetryTimeoutMs ?? 10000,
    zIndex: config.zIndex ?? 999999,
    openInNewTab: config.openInNewTab === true,
  }
}

export function overrideSearchParamsForTab(
  tab: PropertyTab
): Record<string, string> | undefined {
  if (tab === "rent") {
    return { propertySaleType: "Rent" }
  }
  if (tab === "commercial") {
    return { propertyType: "Commercial", propertySaleType: "Sale" }
  }
  return undefined
}

export function warnIfScriptOriginMismatch(searchBaseUrl: string): void {
  const script =
    typeof document !== "undefined"
      ? (document.currentScript as HTMLScriptElement | null)
      : null
  if (!script?.src) {
    return
  }
  try {
    const baseOrigin = new URL(searchBaseUrl).origin
    const scriptOrigin = new URL(script.src).origin
    if (baseOrigin !== scriptOrigin) {
      console.warn(
        `[BppSearch] searchBaseUrl origin (${baseOrigin}) differs from script src origin (${scriptOrigin}).`
      )
    }
  } catch {
    // ignore invalid URLs — normalizeConfig already validated searchBaseUrl
  }
}

const DEFAULT_PLACEHOLDERS = {
  agent: "Search by agent name or location...",
  office: "Search by office name or location...",
  property: "Search by address, city, or ZIP...",
} as const

/**
 * Single source for placeholder copy. Both the modal and the embedded mount
 * resolve it, and the default strings used to be duplicated literals in each.
 */
export function resolvePlaceholder(
  mode: SearchMode,
  defaultTab: PropertyTab | undefined,
  override: string | undefined,
  placeholders: BppSearchConfig["placeholders"]
): string {
  if (mode === "property") {
    return (
      override ??
      placeholders?.[defaultTab ?? "buy"] ??
      DEFAULT_PLACEHOLDERS.property
    )
  }
  return override ?? placeholders?.[mode] ?? DEFAULT_PLACEHOLDERS[mode]
}
